import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PsychologyIcon from '@mui/icons-material/Psychology'
import BuildIcon from '@mui/icons-material/Build'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import { useTheme } from '@mui/material/styles'
import { Collapse, useMediaQuery, Button, Dialog, DialogContent, AppBar, Toolbar, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import { type ToolResultDetail } from '@/core/models/medconnect-agent.model'
import MarkdownProcessor from './MarkdownProcessor'
import { Trans, useTranslation } from 'react-i18next'
import { normalizeUtf8 } from '@/utils/encoding'

interface ToolCallDisplayProps {
    toolInvocation: ToolResultDetail
}

const JsonDisplay: React.FC<{ data: any }> = ({ data }) => {
    if (typeof data === 'undefined' || data === null) {
        return <Typography variant="caption" color="text.secondary">N/A</Typography>
    }

    const isErrorObject = typeof data === 'object' && data !== null && data.error && Object.keys(data).length === 1
    const contentToShow = isErrorObject ? data.error : data

    const formattedJson = JSON.stringify(contentToShow, null, 2)

    return (
        <Paper variant="outlined" sx={{ p: 1.5, width: '100%', backgroundColor: 'action.hover' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                {formattedJson}
            </pre>
        </Paper>
    )
}

export const ToolCallDisplay: React.FC<ToolCallDisplayProps> = ({ toolInvocation }) => {
  const { t } = useTranslation()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const toolName = toolInvocation.tool_name || 'Unknown Tool'
    const loweredToolName = toolName.toLowerCase()
    const isSmartLLM = toolName === 'smartLLMAsk' || loweredToolName.includes('smart') || loweredToolName.includes('llm')
    const [isExpanded, setIsExpanded] = useState(false)
    const [showDetails, setShowDetails] = useState(false)
    const [isFullScreenOpen, setIsFullScreenOpen] = useState(false)

    const hasResult = typeof toolInvocation.result !== 'undefined'
    const isErrorResult = typeof toolInvocation.result === 'object' && toolInvocation.result !== null && toolInvocation.result.error

    const status = hasResult
        ? isErrorResult ? 'error' : 'success'
        : 'pending'

    const statusConfig = {
        pending: {
            label: 'Running...',
            icon: <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />,
            color: 'default' as 'default',
            borderColor: theme.palette.divider
        },
        success: {
            label: 'Success',
            icon: <CheckCircleIcon sx={{ mr: 1 }} />,
            color: 'success' as 'success',
            borderColor: theme.palette.success.light
        },
        error: {
            label: 'Error',
            icon: <ErrorIcon sx={{ mr: 1 }} />,
            color: 'error' as 'error',
            borderColor: theme.palette.error.light
        }
    }

    const currentStatus = statusConfig[status]

    const humanizeToolName = (name: string) => name
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .trim()

    const meta = useMemo(() => {
        if (isSmartLLM) {
            return { label: 'Smart LLM', icon: <PsychologyIcon fontSize="small" /> }
        }
        return { label: humanizeToolName(toolName), icon: <BuildIcon fontSize="small" /> }
    }, [toolName, isSmartLLM])

    const previewText = useMemo(() => {
        if (!isSmartLLM || typeof toolInvocation.result !== 'string') return ''
        const source = normalizeUtf8(toolInvocation.result)
        let plain = source
        try {
            const parsed = JSON.parse(source)
            plain = typeof parsed === 'string' ? parsed : JSON.stringify(parsed)
        } catch (_e) {
            plain = source
        }
        plain = plain
            .replace(/```[\s\S]*?```/g, '')
            .replace(/`[^`]*`/g, '')
            .replace(/^#+\s*/gm, '')
            .replace(/^\s*[-*+]\s+/gm, '')
            .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
            .replace(/[>*_]/g, '')
            .trim()
        const max = isMobile ? 120 : 220
        return plain.length > max ? `${plain.slice(0, max)}…` : plain
    }, [toolInvocation.result, isMobile, isSmartLLM])

    return (
        <>
            <Accordion
                sx={{
                    boxShadow: 'none',
                    border: `1px solid ${currentStatus.borderColor}`,
                    borderRadius: '8px',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                        margin: 0,
                    },
                }}
                expanded={isExpanded}
                onChange={() => { setIsExpanded(!isExpanded) }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        flexDirection: 'row-reverse', // places expand icon on the left
                        '& .MuiAccordionSummary-content': {
                            pl: 1,
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            overflow: 'hidden',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, mr: 2, width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexGrow: 1 }}>
                                {meta.icon}
                                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 'medium', minWidth: 0 }}>
                                    {meta.label}
                                </Typography>
                            </Box>
                            {!isMobile && isSmartLLM && typeof toolInvocation.result === 'string' && (
                                <Button
                                    size="small"
                                    startIcon={<OpenInFullIcon />}
                                    variant="text"
                                    onClick={(e) => { e.stopPropagation(); setIsFullScreenOpen(true) }}
                                    sx={{ mr: 1, flexShrink: 0 }}
                                >
                                    {t('page.medconnectAgent.toolCall.viewFullScreen')}
                                </Button>
                            )}
                            <Chip
                                icon={currentStatus.icon}
                                label={isMobile ? '' : currentStatus.label}
                                color={currentStatus.color}
                                size="small"
                                variant="outlined"
                                sx={{ flexShrink: 0, ml: 'auto' }}
                            />
                        </Box>
                        {!!previewText && !isExpanded && (
                            <Typography variant="body2" color="text.secondary" sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}>
                                {previewText}
                            </Typography>
                        )}
                        {isMobile && isSmartLLM && typeof toolInvocation.result === 'string' && (
                            <Box sx={{ mt: 0.5 }}>
                                <IconButton size="small" aria-label={t('page.medconnectAgent.toolCall.viewFullScreen')} onClick={(e) => { e.stopPropagation(); setIsFullScreenOpen(true) }}>
                                    <OpenInFullIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={showDetails ? 'Hide details' : 'Show details'}
                            onClick={(e) => { e.stopPropagation(); setShowDetails((v) => !v) }}
                        />
                    </Box>

                    <Collapse in={showDetails} unmountOnExit>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
                            {toolInvocation.tool_call_id && (
                                <Box>
                                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        <Trans> Metadata </Trans>
                                    </Typography>
                                    <Chip size="small" variant="outlined" label={`id: ${toolInvocation.tool_call_id}`} />
                                </Box>
                            )}
                            <Box>
                                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    <Trans> Arguments </Trans>
                                </Typography>
                                <JsonDisplay data={toolInvocation.tool_args} />
                            </Box>
                        </Box>
                    </Collapse>
                    {hasResult && (
                        <Box>
                            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                <Trans> Result </Trans>
                            </Typography>
                            {(() => {
                                const result = toolInvocation.result as any
                                if (typeof result === 'string') {
                                    try {
                                        const parsed = JSON.parse(result)
                                        if (parsed && typeof parsed === 'object') {
                                            return <JsonDisplay data={parsed} />
                                        }
                                    } catch (_e) {
                                        // not JSON, fall through to markdown
                                    }
                                    return <MarkdownProcessor content={result} />
                                }
                                return <JsonDisplay data={result} />
                            })()}
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
            {isSmartLLM && typeof toolInvocation.result === 'string' && (
                <Dialog fullScreen open={isFullScreenOpen} onClose={() => setIsFullScreenOpen(false)}>
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={() => setIsFullScreenOpen(false)} aria-label={t('common.close')}>
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                <Trans> Smart LLM Answer </Trans>
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
                        <MarkdownProcessor content={String(toolInvocation.result)} />
                    </DialogContent>
                </Dialog>
            )}
        </>
    )
}

export default ToolCallDisplay 